// Admin users (/admin/users) — search by email + paginated cross-user list.

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Field } from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { useAsync } from "@/lib/useAsync";
import { listAdminUsers } from "@/lib/admin";
import { formatDate, roleLabel } from "@/lib/format";
import type { AdminUser, Paged } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, NoValue, type DataColumn } from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";

const PAGE = 25;

const COLUMNS: readonly DataColumn[] = [
  { label: "Email" },
  { label: "Role" },
  { label: "Referral" },
  { label: "Terdaftar" },
];

export function AdminUsers() {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminUser>>(
    (s) => listAdminUsers({ q: search, limit: PAGE, offset }, s),
    [search, offset],
  );

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    setOffset(0);
    setSearch(input.trim());
  }

  if (loading && !data) return <Loading label="Memuat pengguna…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Pengguna" lede="Cari dan kelola akun pengguna ahcut." />

      <form role="search" aria-label="Cari pengguna" className="flex items-end gap-3" onSubmit={submitSearch}>
        <Field
          label="Cari email"
          className="flex-1 sm:max-w-sm"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="mis. nama@domain.com"
        />
        <Button type="submit" variant="quiet" disabled={loading}>
          Cari
        </Button>
      </form>

      {!data || data.items.length === 0 ? (
        <EmptyState title="Tidak ada pengguna">
          {search ? `Tidak ada pengguna dengan email mengandung “${search}”.` : "Belum ada pengguna terdaftar."}
        </EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable label="Daftar pengguna" columns={COLUMNS} stackBelow="40rem" busy={loading}>
              {data.items.map((u) => (
                <DataRow key={u.id}>
                  <DataCell label="Email" className="whitespace-nowrap">
                    <Link to={`/admin/users/${u.id}`}>{u.email || "Akun dianonimkan"}</Link>
                  </DataCell>
                  <DataCell label="Role">
                    <Badge tone={u.role === "admin" ? "pro" : "neutral"}>{roleLabel(u.role)}</Badge>
                  </DataCell>
                  <DataCell label="Referral" className={dataCellClass}>
                    {u.referred_by_code ?? <NoValue label="Tanpa kode referral" />}
                  </DataCell>
                  <DataCell label="Terdaftar" className={dataCellClass}>
                    {formatDate(u.created_at)}
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
